import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Link from '@material-ui/core/Link';

import prizesClient from '../../clients/prizes';
import ProfileLayout from './ProfileLayout';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

const EXTRA = {
  'lt-LT': {
    pageTitle: 'Prizai',
    back: 'Atgal į profilį',
    distance: 'Atstumas',
    object: 'Objektas',
    openMaps: 'Žemėlapis',
    openApp: 'Atidaryti programėlėje',
    noLocation: 'Įjunkite vietos nustatymą, jei norite matyti atstumą iki objekto.',
    empty: 'Prizų nėra.',
    description: 'Aprašymas',
    qrForPrize: 'QR kodai prizui',
    qrEnabled: 'aktyvūs šiam prizui',
    images: 'Nuotraukos',
  },
  'en-EU': {
    pageTitle: 'Prizes',
    back: 'Back to profile',
    distance: 'Distance',
    object: 'Place',
    openMaps: 'Map',
    openApp: 'Open in app',
    noLocation: 'Enable location to see distance to each place.',
    empty: 'No prizes yet.',
    description: 'Description',
    qrForPrize: 'QR codes for this prize',
    qrEnabled: 'active for this prize',
    images: 'Photos',
  },
};

function formatDistanceMeters(m) {
  if (m == null || Number.isNaN(m)) return '—';
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

function plainTextFromRich(s) {
  if (s == null || typeof s !== 'string') return '';
  return s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function imageFullUrl(rel) {
  if (!rel || typeof rel !== 'string') return '';
  if (rel.startsWith('http')) return rel;
  return `${SERVER_URL}${rel}`;
}

const ProfilePrizes = ({ locale, basePath }) => {
  const history = useHistory();
  const t = EXTRA[locale] || EXTRA['en-EU'];
  const [coords, setCoords] = useState(null);
  const [geoTried, setGeoTried] = useState(false);
  const [prizes, setPrizes] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const loadPrizes = useCallback(
    async (lat, lng) => {
      setLoadError(null);
      try {
        const params = { locale };
        if (lat != null && lng != null) {
          params.lat = lat;
          params.lng = lng;
        }
        const raw = await prizesClient.findForProfile(params);
        const list = Array.isArray(raw) ? raw : [];
        setPrizes(list);
      } catch (e) {
        setLoadError(e);
        setPrizes([]);
      }
    },
    [locale]
  );

  useEffect(() => {
    if (!navigator?.geolocation) {
      setGeoTried(true);
      loadPrizes(null, null);
      return undefined;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoTried(true);
        loadPrizes(pos.coords.latitude, pos.coords.longitude);
      },
      () => {
        setGeoTried(true);
        loadPrizes(null, null);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
    return undefined;
  }, [loadPrizes]);

  const handleBack = () => history.push(basePath);

  return (
    <ProfileLayout locale={locale} title={t.pageTitle}>
      <Button onClick={handleBack} style={{ color: '#fff', marginBottom: 16, textTransform: 'none' }}>
        {t.back}
      </Button>

      {!geoTried || prizes === null ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress style={{ color: '#fff' }} />
        </Box>
      ) : (
        <>
          {coords == null && (
            <Typography style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.875rem', marginBottom: 16 }}>
              {t.noLocation}
            </Typography>
          )}
          {loadError && (
            <Typography color="error" paragraph>
              {locale === 'lt-LT' ? 'Nepavyko įkelti prizų.' : 'Could not load prizes.'}
            </Typography>
          )}
          {prizes.length === 0 && !loadError ? (
            <Typography style={{ color: 'rgba(255,255,255,0.85)' }}>{t.empty}</Typography>
          ) : (
            prizes.map((row, idx) => {
              const desc = plainTextFromRich(row.Description != null ? String(row.Description) : '');
              const spendList = Array.isArray(row.spendCodesForPrize) ? row.spendCodesForPrize : [];
              const spendEnabled = spendList.filter((c) => c.enabledForPrize && c.globallyEnabled);
              const imgs = Array.isArray(row.prizeImages) ? row.prizeImages : [];

              return (
                <Box key={row.documentId || idx} mb={2}>
                  {idx > 0 && <Divider style={{ marginBottom: 16, backgroundColor: 'rgba(255,255,255,0.12)' }} />}
                  <Typography variant="subtitle1" style={{ color: '#fff', fontWeight: 600 }}>
                    {row.Name}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.85)', marginTop: 4 }}>
                    {t.object}: {row.object?.Title ?? '—'}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
                    {t.distance}: {formatDistanceMeters(row.distanceToObjectMeters)}
                  </Typography>
                  {desc ? (
                    <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.7)', marginTop: 8 }}>
                      {t.description}: {desc.slice(0, 400)}
                      {desc.length > 400 ? '…' : ''}
                    </Typography>
                  ) : null}
                  {imgs.length > 0 ? (
                    <Box mt={1}>
                      <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.65)' }}>
                        {t.images}
                      </Typography>
                      <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 6 }}>
                        {imgs.map((img, i) => {
                          const href = imageFullUrl(img.url);
                          if (!href) return null;
                          return (
                            <Box
                              key={img.documentId || i}
                              component="a"
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: 'block' }}
                            >
                              <img
                                src={href}
                                alt={img.alternativeText || ''}
                                style={{
                                  width: 96,
                                  height: 96,
                                  objectFit: 'cover',
                                  borderRadius: 8,
                                  border: '1px solid rgba(255,255,255,0.2)',
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ) : null}
                  {spendList.length > 0 ? (
                    <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.65)', marginTop: 10 }}>
                      {t.qrForPrize}:{' '}
                      {spendEnabled.length > 0
                        ? spendEnabled.map((c) => c.Name || c.documentId).join(', ')
                        : locale === 'lt-LT'
                          ? 'nėra aktyvių'
                          : 'none active'}
                      {spendEnabled.length > 0 ? ` (${t.qrEnabled})` : ''}
                    </Typography>
                  ) : null}
                  <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginTop: 12 }}>
                    {row.mapsUrl ? (
                      <Button
                        component="a"
                        href={row.mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="small"
                        style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', textTransform: 'none' }}
                      >
                        {t.openMaps}
                      </Button>
                    ) : null}
                    {row.objectDeepLink ? (
                      <Link href={row.objectDeepLink} style={{ alignSelf: 'center', color: '#7ee787' }}>
                        {t.openApp}
                      </Link>
                    ) : null}
                  </Box>
                </Box>
              );
            })
          )}
        </>
      )}
    </ProfileLayout>
  );
};

ProfilePrizes.propTypes = {
  locale: PropTypes.string.isRequired,
  basePath: PropTypes.string.isRequired,
};

export default ProfilePrizes;
